@if (!isAdminRole) {
  <section class="release-shell" [class.has-sticky-save]="hasPendingReleaseChanges">
    <div class="release-card muted">
      <h1>Release Management</h1>
      <p>This section is available only for admin roles.</p>
    </div>
  </section>
} 
@else {
  <section class="release-shell" [class.has-sticky-save]="hasPendingReleaseChanges">
    @if (loader.isLoading) {
      <div class="loader-class" aria-live="polite">
        <mat-spinner diameter="26"></mat-spinner>
        <div class="loader-message">{{ loader.message }}</div>
      </div>
    }
    <div class="release-card">
      <div class="release-card-header">
        <h1>Release Management</h1>
        <button type="button" class="create-release-btn" (click)="openCreateReleaseDialog()">Create New Release</button>
      </div>
      <div class="filter-row">
        <div class="field field-inline">
          <label for="release-ticket-count">Tickets per page</label>
          <select id="release-ticket-count" [(ngModel)]="selectedTicketCount" (ngModelChange)="onTicketCountChange()" [disabled]="loader.isLoading">
            @for (count of ticketCountOptions; track count) {
              <option [ngValue]="count">{{ count }}</option>
            }
          </select>
        </div>
      </div>

      <div class="flow-guide" aria-label="Release stage progression guide">
        <button type="button" class="flow-guide-header" 
            (click)="isStageFlowExpanded = !isStageFlowExpanded" [attr.aria-expanded]="isStageFlowExpanded">
          <span class="flow-guide-title">Release Stage Flow</span>
          <i class="pi flow-guide-icon" [class.pi-chevron-up]="isStageFlowExpanded"
            [class.pi-chevron-down]="!isStageFlowExpanded">
        </i>
        </button>
        @if (isStageFlowExpanded) {
          <div class="flow-guide-body">
            <div class="flow-track">
              @for (stage of releaseFlowStages; track stage; let isLast = $last) {
                <span class="flow-node">{{ stage }}</span>
                @if (!isLast) {
                  <span class="flow-arrow">&rarr;</span>
                }
              }
            </div>
            <p class="flow-note">Versions progress only in this direction as stage flags are promoted.</p>
          </div>
        }
      </div>

    </div>

    @if (selectedUseCaseId) {
      <div class="release-grid">
        <aside class="release-card left-panel">
          <h4>New Tickets</h4>
          <p class="subtle">Select tickets to assign for release</p>
          <div class="ticket-list">
            @for (ticket of newTickets; track ticket.feedback_id) {
              <label class="ticket-item ticket-item-selectable">
                <input
                  type="checkbox"
                  [checked]="isTicketSelected(ticket)"
                  (change)="toggleTicketSelection(ticket, $any($event.target).checked)">
                <div class="ticket-content">
                  <div class="ticket-header">
                    <strong>Ticket <a href="" class="ticket-link" (click)="$event.preventDefault(); openTicketPopup(ticket.feedback_id ?? 0)">{{ ticket.feedback_id }}</a></strong>
                    <span class="status-badge" [class]="ticketStatusClass(ticket.status_id)">{{ getTicketStatusLabel(ticket) }}</span>
                  </div>
                  <span>{{ ticket.title }}</span>
                </div>
              </label>
            } 
            @empty {
              <div class="empty-state">No unassigned tickets in the current use case.</div>
            }
          </div>

          @if (ticketTotalCount > 0) {
            <div class="ticket-pagination" aria-label="New tickets pagination">
              <button type="button" class="pager-nav" (click)="prevTicketPage()" [disabled]="ticketPage === 1 || loader.isLoading">
                ‹
              </button>
              <div class="pager-numbers">
                @for (pageNumber of visiblePageNumbers; track pageNumber) {
                  <button
                    type="button"
                    class="pager-number"
                    [class.active]="ticketPage === pageNumber"
                    [disabled]="loader.isLoading"
                    (click)="goToTicketPage(pageNumber)">
                    {{ pageNumber }}
                  </button>
                }
              </div>
              <button type="button" class="pager-nav" (click)="nextTicketPage()" [disabled]="ticketPage >= ticketTotalPages || loader.isLoading">
                ›
              </button>
              <span class="pager-meta">{{ ticketPage }} / {{ ticketTotalPages }}</span>
            </div>
          }

          <div class="assign-panel">
            <label for="assign-version">Assign to Release:</label>
            <select id="assign-version" [(ngModel)]="selectedAssignVersionId" [disabled]="loader.isLoading || assignableVersions.length === 0">
              <option [ngValue]="null" disabled>Select release</option>
              @for (version of assignableVersions; track version.version_id) {
                <option [ngValue]="version.version_id">{{ version.version_name }}</option>
              }
            </select>
            <button type="button" class="assign-btn" (click)="assignSelectedTickets()" [disabled]="!canAssignSelectedTickets">
              Assign {{ selectedNewTicketCount }} Ticket(s)
            </button>
          </div>
        </aside>

        <section class="release-card right-panel">
          <h4>Release Pipeline</h4>
          <div class="pipeline-list">
            @for (version of versions; track version.version_id) {
              <article
                class="pipeline-item"
                [class.selected]="selectedPipelineVersionId === version.version_id"
                [style.borderColor]="getVersionColor(version)"
                [style.boxShadow]="'0 0px 0px 1px ' + getVersionColor(version)">
                <div class="pipeline-head" (click)="selectPipelineVersion(version.version_id)" 
                  (keydown.enter)="selectPipelineVersion(version.version_id)">
                  <button type="button" class="pipeline-select-btn">
                    <div class="title-row">
                      <h5>{{ version.version_name }}</h5>
                      <span class="stage-pill" [style.backgroundColor]="getVersionColor(version)">
                        {{ getVersionStageName(version) }}
                      </span>
                    </div>
                    <div class="pipeline-select-footer">
                      <p>{{ getAssignedTickets(version.version_id).length }} tickets assigned</p>
                    </div>
                  </button>

                  <div class="pipeline-head-actions">
                    @if (canPromote(version)) {
                      <button type="button" class="promote-btn" [style.backgroundColor]="getNextActionColor(version)"
                        [disabled]="loader.isLoading || hasPendingReleaseChanges"
                        (click)="$event.stopPropagation(); onVersionStatusClick(version)">
                        {{ getNextActionLabel(version) }}
                      </button>
                    }
                    <button type="button" class="delete-release-btn" [disabled]="loader.isLoading"
                      aria-label="Delete release"
                      title="Delete release"
                      (click)="confirmDeleteVersion($event, version)">
                      <i class="pi pi-trash" aria-hidden="true"></i>
                    </button>
                    <button type="button" class="pipeline-toggle-btn" 
                        (click)="$event.stopPropagation(); selectPipelineVersion(version.version_id)"
                        [attr.aria-expanded]="selectedPipelineVersionId === version.version_id">
                      <i class="pi" [class.pi-chevron-up]="selectedPipelineVersionId === version.version_id"
                        [class.pi-chevron-down]="selectedPipelineVersionId !== version.version_id">
                      </i>
                    </button>
                  </div>
                </div>

                @if (selectedPipelineVersionId === version.version_id) {
                  <div class="pipeline-body" style="max-height: 500px;overflow-y: auto;">
                    <h6>Assigned Tickets</h6>
                    @for (ticket of getAssignedTickets(version.version_id); track ticket.feedback_id) {
                      <div class="assigned-item"
                        [class.assigned-item-marked-delete]="isTicketMarkedForDelete(ticket.feedback_id)"
                        [class.assigned-item-invalid]="showRequiredFieldHighlights && hasMissingRequiredFields(ticket)"
                        [class.assigned-item-new]="isTicketNewlyAssigned(ticket.feedback_id)">
                        <div class="assigned-item-header">
                          <div>
                            <div class="ticket-header">
                              <strong>Ticket <a href="" class="ticket-link" (click)="$event.preventDefault(); openTicketPopup(ticket.feedback_id)">{{ ticket.feedback_id }}</a></strong>
                              <span class="status-badge" [class]="ticketStatusClass(ticket.status_id)">{{ getTicketStatusLabel(ticket) }}</span>
                            </div>
                            <span>{{ ticket.title }}</span>
                          </div>
                          @if (isTicketNewlyAssigned(ticket.feedback_id) && !isTicketMarkedForDelete(ticket.feedback_id)) {
                            <span class="unsaved-tag">Unsaved</span>
                          }
                        </div>
                        <div class="assigned-controls">
                          <div class="assigned-field">
                            <label>Select Priority</label>
                            <select
                              [(ngModel)]="ticket.priority_id"
                              [disabled]="loader.isLoading || isTicketMarkedForDelete(ticket.feedback_id)">
                              <option [ngValue]="null" disabled>Select Priority</option>
                              @for (priority of priorityOptions; track priority.id) {
                                <option [ngValue]="priority.id">{{ priority.name }}</option>
                              }
                            </select>
                          </div>

                          <div class="assigned-field">
                            <label>Select Developer</label>
                            <select
                              [(ngModel)]="ticket.assigned_user"
                              [disabled]="loader.isLoading || loadingValidationUsers || isTicketMarkedForDelete(ticket.feedback_id)">
                              <option [ngValue]="null" disabled>Select Developer</option>
                              @for (developer of developerOptions; track developer.id) {
                                <option [ngValue]="developer.id">{{ developer.user_name }}</option>
                              }
                            </select>
                          </div>

                          <div class="assigned-field">
                            <label>Select Ticket Status</label>
                            <select
                              [(ngModel)]="ticket.status_id"
                              [disabled]="loader.isLoading || isTicketMarkedForDelete(ticket.feedback_id)">
                              <option [ngValue]="null" disabled>Select Ticket Status</option>
                              @for (status of ticketStatusOptions; track status.id) {
                                <option [ngValue]="status.id">{{ status.name }}</option>
                              }
                            </select>
                          </div>

                          <div class="assigned-field">
                            <label>Select Validator</label>
                            <select
                              [(ngModel)]="ticket.validated_user"
                              [disabled]="loader.isLoading || loadingValidationUsers || isTicketMarkedForDelete(ticket.feedback_id)">
                              <option [ngValue]="null" disabled>Select Validator</option>
                              @for (validator of validatorOptions; track validator.id) {
                                <option [ngValue]="validator.id">{{ validator.user_name }}</option>
                              }
                            </select>
                          </div>

                          <button
                            type="button"
                            class="remove-ticket-btn"
                            [class.remove-ticket-btn--undo]="isTicketMarkedForDelete(ticket.feedback_id)"
                            [disabled]="loader.isLoading"
                            [title]="isTicketMarkedForDelete(ticket.feedback_id) ? 'Undo remove ticket' : 'Remove ticket'"
                            (click)="isTicketMarkedForDelete(ticket.feedback_id) ? undoUnassignTicket(ticket) : unassignTicket(ticket)">
                            @if (isTicketMarkedForDelete(ticket.feedback_id)) {
                              <i class="pi pi-undo"></i>
                            } @else {
                              ✕
                            }
                          </button>
                        </div>
                      </div>
                    }
                    @empty {
                      <div class="empty-state">No tickets assigned.</div>
                    }
                  </div>
                }
              </article>
            }
            @empty {
              <div class="empty-state">No versions found for the selected use case.</div>
            }
          </div>

        </section>
      </div>

      @if (selectedUseCaseId && hasPendingReleaseChanges) {
        <div class="sticky-save-bar" aria-live="polite">
          <div class="sticky-save-content">
            <span class="sticky-save-text">You have unsaved release changes.</span>
            <div class="sticky-save-actions">
              <button
                type="button"
                class="reset-release-btn"
                (click)="resetReleaseChanges()"
                [disabled]="loader.isLoading || !hasPendingReleaseChanges">
                Reset
              </button>
              <button
                type="button"
                class="save-release-btn sticky-save-btn"
                (click)="saveReleaseChanges()"
                [disabled]="loader.isLoading || !hasPendingReleaseChanges">
                Save all changes
              </button>
            </div>
          </div>
        </div>
      }

      @if (selectedTicketPopupData) {
        <dialog [open]="selectedTicketPopupData !== null" class="ticket-popup" aria-modal="false" aria-label="Ticket details popup">
          <div class="ticket-popup-header">
            <h4>#{{ selectedTicketPopupData.feedback_id }} - {{ selectedTicketPopupData.title }}</h4>
            <button type="button" class="popup-close-icon" (click)="closeTicketPopup()" aria-label="Close popup">X</button>
          </div>

          <div class="details-section popup-details-section">
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Use Case</span>
                <span class="detail-value">{{ getPopupUseCaseLabel(selectedTicketPopupData) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Category</span>
                <span class="detail-value">{{ getPopupCategoryLabel(selectedTicketPopupData) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Request Type</span>
                <span class="detail-value">{{ getPopupRequestTypeLabel(selectedTicketPopupData) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Application</span>
                <span class="detail-value">{{ getPopupApplicationLabel(selectedTicketPopupData) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Assigned To</span>
                <span class="detail-value">{{ selectedTicketPopupData?.assigned_user_name ?? '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Assigned Team</span>
                <span class="detail-value">{{ getPopupAssignedTeamLabel(selectedTicketPopupData) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Company</span>
                <span class="detail-value">{{ selectedTicketPopupData.company || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Plant</span>
                <span class="detail-value">{{ selectedTicketPopupData.plant || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Shop</span>
                <span class="detail-value">{{ selectedTicketPopupData.shop || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Submitted</span>
                <span class="detail-value">{{ selectedTicketPopupData.creation_date || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Submitter</span>
                <span class="detail-value">{{ selectedTicketPopupData.requested_user_name || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Ticket Status</span>
                <span class="detail-value">{{ getTicketStatusLabel(selectedTicketPopupData) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Version</span>
                <span class="detail-value">{{ selectedTicketPopupData?.version ?? '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Priority</span>
                <span class="detail-value">{{ getPriorityLabel(selectedTicketPopupData) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Validation Assignee</span>
                <span class="detail-value">{{ selectedTicketPopupData?.validated_user_name ?? '-' }}</span>
              </div>
            </div>
          </div>

          <div class="ticket-popup-footer">
            <button type="button" class="popup-close-btn" (click)="closeTicketPopup()">Close</button>
          </div>
        </dialog>
      }
    }
  </section>
}

 <p-dialog
    [(visible)]="isCreateReleaseDialogVisible"
    [header]="'Create New Release'"
    [modal]="true"
    [closable]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '500px', 'max-width': '95vw' }">

    <form id="create-release-form" class="create-release-form" #createReleaseForm="ngForm" (ngSubmit)="submitCreateRelease()">
      <label for="new-release-name">Version Name <span class="required-marker" aria-hidden="true">*</span></label>
      <input id="new-release-name" name="versionName" type="text" [(ngModel)]="newReleaseName" required [disabled]="loader.isLoading">

      <label for="new-release-description">Description <span class="required-marker" aria-hidden="true">*</span></label>
      <textarea id="new-release-description" name="versionDescription" [(ngModel)]="newReleaseDescription" required [disabled]="loader.isLoading" rows="4"></textarea>

      <label for="new-release-usecase-id">Use Case <span class="required-marker" aria-hidden="true">*</span></label>
      <select id="new-release-usecase-id" name="usecaseId" [(ngModel)]="newReleaseUseCaseId" required [disabled]="loader.isLoading">
        <option [ngValue]="null" disabled>Select use case</option>
        @for (useCase of createReleaseUseCaseOptions; track useCase.id) {
          <option [ngValue]="useCase.id">{{ useCase.name }}</option>
        }
      </select>

      <label for="new-release-target-date">Target Date <span class="required-marker" aria-hidden="true">*</span></label>
      <p-datepicker id="new-release-target-date" name="targetDate" #targetDate="ngModel"
        [(ngModel)]="newReleaseTargetDate" [minDate]="minReleaseTargetDate" dateFormat="yy-mm-dd"
        appendTo="body" showIcon="true" [readonlyInput]="true" placeholder="Select target date"
        [disabled]="loader.isLoading" required fluid>
      </p-datepicker>
    </form>

    <ng-template pTemplate="footer">
      <button type="button" class="popup-close-btn" (click)="closeCreateReleaseDialog()">Cancel</button>
      <button type="button" class="btn-dialog-save" (click)="createNewReleaseEntry()" [disabled]="!canSubmitCreateRelease"
        title="Release creation is not connected to the backend yet">
        Create
      </button>
    </ng-template>
  </p-dialog>

 <p-dialog
    [(visible)]="isSummaryPopupVisible"
    [header]="'Summary'"
    [modal]="true"
    [closable]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '540px', 'max-width': '95vw' }">

    <div class="" style="height: 100%;">
      <quill-editor #editor [(ngModel)]="summary" [modules]="quillModules" [style]="{height: '150px'}"
          placeholder="Describe what you saw, heard or experienced...">
      </quill-editor>
    </div>

    <ng-template pTemplate="footer">
      <button type="button" class="btn-dialog-save" (click)="addSummary()" [disabled]="loader.isLoading">
        Save
      </button>
    </ng-template>
  </p-dialog>